/**
 * FanOutSection — renders fan-out parent steps and their child unit reports inside
 * the workflow run debugger Graph tab (Spec 82 S6).
 *
 * Non-fan-out runs render nothing (the section returns null), so existing runs are
 * unaffected. Each fan-out parent shows aggregate counts, a readable status summary,
 * and an expand/collapse affordance for its child units. Only compact, summary-only
 * reports are surfaced — never raw child-agent prompts, messages, logs, transcripts,
 * or tool I/O.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Network } from 'lucide-react';
import { Badge } from '../../../components/ui/Badge.js';
import { Button } from '../../../components/ui/Button.js';
import { Chip, type ChipTone } from '../../../components/ui/Chip.js';
import { FanOutUnitRow } from './FanOutUnitRow.js';
import {
  type DebugStep,
  type FanOutReport,
  fanOutSummary,
  humanizeStatus,
  statusBadgeVariant,
} from './run-detail-types.js';

// Aggregate-count chips. Zero-valued counts are dimmed (neutral tone) but still shown so
// all-skipped / all-failed parents stay readable.
const COUNT_CONFIG: Array<{ key: keyof FanOutReport['counts']; label: string; tone: ChipTone }> = [
  { key: 'succeeded', label: 'ok', tone: 'green' },
  { key: 'failed', label: 'failed', tone: 'danger' },
  { key: 'running', label: 'running', tone: 'blue' },
  { key: 'pending', label: 'pending', tone: 'neutral' },
  { key: 'skipped', label: 'skipped', tone: 'neutral' },
  { key: 'retried', label: 'retried', tone: 'warn' },
];

interface FanOutStepCardProps {
  step: DebugStep;
  fanOut: FanOutReport;
  /** Initial expansion override (used by tests). Defaults to expanded when any unit failed. */
  defaultExpanded?: boolean;
}

export const FanOutStepCard: React.FC<FanOutStepCardProps> = ({ step, fanOut, defaultExpanded }) => {
  const total = fanOut.units.length;
  const [expanded, setExpanded] = useState(defaultExpanded ?? fanOut.counts.failed > 0);
  const bodyId = `fanout-units-${step.stepId ?? step.id}`;

  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden mb-4">
      {/* Card header — parent identity + status */}
      <div className="flex items-center justify-between gap-2.5 px-4 py-3 border-b border-border bg-surface-alt">
        <span className="font-mono text-[10.5px] uppercase tracking-[1.2px] text-muted flex items-center gap-2 min-w-0">
          <Network size={14} className="text-faint shrink-0" />
          <span className="truncate">Fan-out · {step.stepName}</span>
        </span>
        <Badge variant={statusBadgeVariant(step.status)} dot>
          {humanizeStatus(step.status)}
        </Badge>
      </div>

      <div className="px-4 py-3">
        {/* Aggregate counts */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {COUNT_CONFIG.map(({ key, label, tone }) => (
            <Chip key={key} tone={fanOut.counts[key] > 0 ? tone : 'neutral'}>
              {fanOut.counts[key]} {label}
            </Chip>
          ))}
        </div>

        {/* Readable parent status summary */}
        <p className="mt-2 text-[12px] text-muted">{fanOutSummary(fanOut.counts, total)}</p>

        {/* Expand / collapse child units */}
        {total > 0 ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls={bodyId}
              className="mt-2"
            >
              {expanded ? <ChevronDown size={13} aria-hidden="true" /> : <ChevronRight size={13} aria-hidden="true" />}
              {expanded ? 'Hide' : 'Show'} {total} unit{total !== 1 ? 's' : ''}
            </Button>
            {expanded && (
              <ul id={bodyId} className="mt-2 border border-border rounded-lg overflow-hidden">
                {fanOut.units.map((unit) => (
                  <FanOutUnitRow key={unit.unitId} unit={unit} />
                ))}
              </ul>
            )}
          </>
        ) : (
          <p className="mt-2 text-[11.5px] text-faint">No units were dispatched.</p>
        )}
      </div>
    </div>
  );
};

interface FanOutSectionProps {
  steps: DebugStep[];
}

export const FanOutSection: React.FC<FanOutSectionProps> = ({ steps }) => {
  const fanOutSteps = steps.filter((s): s is DebugStep & { fanOut: FanOutReport } => s.fanOut != null);
  if (fanOutSteps.length === 0) return null;

  return (
    <>
      {fanOutSteps.map((step) => (
        <FanOutStepCard key={step.stepId ?? step.id} step={step} fanOut={step.fanOut} />
      ))}
    </>
  );
};
